import { IAppContext } from 'App/AppContext';
import { FilterTaskGroupKeys } from 'components/Tasks/Filter/FilterTaskGroupKeys';
import { controlIdSearchPrepend, themeIdSearchPrepend } from 'components/Tasks/Filter/FilterTasksHelper';
import { EntityTypes } from 'models/entity';
import { Integration } from 'models/integrations/integration';
import { TaskWorkableTypes } from 'models/tasks/task';
import { TaskTagFilterGroupAnd } from 'models/userSetting';

const applyMultipleTagFilter = (
  tagFilterList: string[],
  integration: Integration,
  useAndForTagGroups: boolean,
  appContext: IAppContext,
): boolean => {
  if (useAndForTagGroups) {
    const tags = appContext.globalDataCache.tags.getItemsForId(tagFilterList.map((id) => Number(id)));

    for (let idx = 0; idx < tagFilterList.length; idx++) {
      const tag = appContext.globalDataCache.tags.get(Number(tagFilterList[idx]));
      const duplicates = tags.filter((t) => t.tagName === tag.tagName);
      if (duplicates.length === 1) {
        //AND
        if (!integration.kpi.tagIds?.some((id) => id === tag.tagId)) {
          return false;
        }
      } else {
        //OR
        if (!integration.kpi.tagIds?.some((t) => duplicates?.some((tag) => tag.tagId === t))) {
          return false;
        }
      }
    }

    return true;
  }

  //use OR
  return tagFilterList.some((t) => integration.kpi.tagIds?.some((id) => id.toString() === t));
};

const applyMultipleTagGroupFilter = (
  tagGroupFilterList: string[],
  integration: Integration,
  appContext: IAppContext,
): boolean => {
  const tags = appContext.globalDataCache.tags.getItemsForId(integration.kpi.tagIds);

  return tagGroupFilterList.some((t) => tags?.some((tag) => tag.tagName.toLowerCase() === t.toLowerCase()));
};

export const filterIntegrations = (
  filters: string[],
  integrations: Integration[],
  appContext: IAppContext,
): Integration[] => {
  if (!integrations || integrations.length === 0) return integrations;

  const output: Integration[] = [];
  const tagFilterList: string[] = [];
  const tagGroupFilterList: string[] = [];
  const controlFilterList: string[] = [];
  const themeFilterList: string[] = [];
  const objectiveFilterList: string[] = [];
  const workableTypeFilterList: string[] = [];

  let hasUnsupportedFilter: boolean = false;

  //get setting from user settings
  let useAndForTagGroups = appContext.globalDataCache.userSettings.get(TaskTagFilterGroupAnd) as boolean;

  filters.forEach((filter: string) => {
    const filterGroup = filter.split('$');
    const filterType = filterGroup[0] + '$';
    const filterValue = filterGroup[1];

    switch (filterType) {
      case FilterTaskGroupKeys.tag:
        tagFilterList.push(filterValue);
        break;
      case FilterTaskGroupKeys.tagGroup:
        tagGroupFilterList.push(filterValue);
        break;
      case FilterTaskGroupKeys.context:
        if (filterValue.startsWith(controlIdSearchPrepend)) controlFilterList.push(filterValue);
        else if (filterValue.startsWith(themeIdSearchPrepend)) themeFilterList.push(filterValue);

        const typeid = filterValue.split('_');
        const type = Number(typeid[0]);
        const id = Number(typeid[1]);
        if (type && id && !isNaN(type) && !isNaN(id)) {
          switch (type) {
            case EntityTypes.Objective:
              objectiveFilterList.push(id.toString());
              break;
            default:
              break;
          }
        }

        break;
      case FilterTaskGroupKeys.useAndForTagGroups:
        //get the useAndForTagGroups filter from the filter
        //if not found, get it from the user settings
        useAndForTagGroups = filterValue === 'true';

        break;
      case FilterTaskGroupKeys.workableTaskType:
        workableTypeFilterList.push(filterValue);
        break;
      default:
        hasUnsupportedFilter = true;
        break;
    }
  });

  // If there is an unsupported filter, return nothing
  if (hasUnsupportedFilter) {
    return output;
  }

  integrations.forEach((integration: Integration) => {
    let tagFilterCheck = true;
    let tagGroupFilterCheck = true;
    let controlFilterCheck = true;
    let themeFilterCheck = true;
    let objectiveFilterCheck = true;
    let workableTypeFilterCheck = true;

    if (tagFilterList.length > 0 && !applyMultipleTagFilter(tagFilterList, integration, useAndForTagGroups, appContext))
      tagFilterCheck = false;

    if (tagGroupFilterList.length > 0 && !applyMultipleTagGroupFilter(tagGroupFilterList, integration, appContext))
      tagGroupFilterCheck = false;

    //
    // Context filters
    //
    if (
      controlFilterList.length > 0 &&
      !controlFilterList.some(
        (t) => integration.kpi.controlIds?.some((id) => controlIdSearchPrepend + id.toString() === t),
      )
    ) {
      controlFilterCheck = false;
    }

    if (
      themeFilterList.length > 0 &&
      !themeFilterList.some((t) => integration.kpi.controlIds?.some((id) => themeIdSearchPrepend + id.toString() === t))
    ) {
      themeFilterCheck = false;
    }

    if (
      objectiveFilterList.length > 0 &&
      !objectiveFilterList.some((t) => integration.kpi.objectiveIds?.some((id) => id.toString() === t))
    ) {
      objectiveFilterCheck = false;
    }

    if (
      workableTypeFilterList.length > 0 &&
      !workableTypeFilterList.some((t) => t === TaskWorkableTypes.Recurring.toString())
    ) {
      workableTypeFilterCheck = false;
    }

    // Final check
    if (
      tagFilterCheck &&
      tagGroupFilterCheck &&
      controlFilterCheck &&
      themeFilterCheck &&
      objectiveFilterCheck &&
      workableTypeFilterCheck
    ) {
      output.push(integration);
    }
  });

  return output;
};
